import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Checkbox, FormControlLabel, Radio, RadioGroup,
  Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, Container, Grid, IconButton, InputLabel, FormControl, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, TableSortLabel
} from '@mui/material';
import { Edit, Delete, Lock, LockOpen } from '@mui/icons-material';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

function UserAdmin() {
  const { t } = useAdminLanguage();

  // Fonction pour obtenir les options de grades traduites
  const getGradeOptions = () => [
    { value: 'AAGP', label: t('Aspirant Agent de Police') },
    { value: 'AGP', label: t('Agent de Police') },
    { value: '1AGP', label: t('Premier Agent de Police') },
    { value: 'ADAGP', label: t('Aspirant Agent de sécurisation de Police') },
    { value: 'DAGP', label: t('Agent de sécurisation de Police') },
    { value: '1DAGP', label: t('Premier Agent de sécurisation de Police') },
    { value: 'ABASP', label: t('Aspirant Assistant de Sécurisation de Police') },
    { value: 'BASP', label: t('Assistant de Sécurisation de Police') },
    { value: '1BASP', label: t('Premier Assistant de Sécurisation de Police') },
    { value: 'BCSP', label: t('Coordonnateur de Sécurisation de Police') },
    { value: '1BCSP', label: t('Premier Coordonnateur de Sécurisation de Police') },
    { value: 'AINP', label: t('Aspirant Inspecteur de Police') },
    { value: 'INP', label: t('Inspecteur de Police') },
    { value: '1INP', label: t('Premier Inspecteur de Police') },
    { value: 'AINPP', label: t('Aspirant Inspecteur Principal de Police') },
    { value: 'INPP', label: t('Inspecteur Principal de Police') },
    { value: '1INPP', label: t('Premier Inspecteur Principal de Police') },
    { value: 'ACP', label: t('Aspirant Commissaire de Police') },
    { value: 'CP', label: t('Commissaire de Police') },
    { value: '1CP', label: t('Premier Commissaire de Police') },
    { value: 'CDP', label: t('Commissaire Divisionnaire de Police') },
    { value: '1CDP', label: t('Premier Commissaire Divisionnaire de Police') }
  ];
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    matricule: '',
    password: '',
    lastname: '',
    firstname: '',
    service: '',
    rank: '',
    sex: '',
    language: 'fr',
    opj: false,
    opa: false,
    moderator: false,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false); // État pour la boîte de dialogue
  const [userToDelete, setUserToDelete] = useState(null); // Utilisateur à supprimer
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('matricule');
  const gradeOptions = useMemo(() => getGradeOptions(), [t]);

  const normalizeRankForForm = (rank) => {
    if (rank === null || rank === undefined) return '';

    const normalized = rank.toString().trim();
    if (!normalized) return '';

    const exactCode = gradeOptions.find((option) => option.value === normalized);
    if (exactCode) return exactCode.value;

    const codeInsensitive = gradeOptions.find(
      (option) => option.value.toLowerCase() === normalized.toLowerCase()
    );
    if (codeInsensitive) return codeInsensitive.value;

    const fromLabel = gradeOptions.find(
      (option) => option.label.toLowerCase() === normalized.toLowerCase()
    );
    if (fromLabel) return fromLabel.value;

    // Fallback: garder la valeur existante pour éviter un champ visuellement vide.
    return normalized;
  };

  const columns = useMemo(() => ([
    { id: 'matricule', label: t('Matricule'), sortable: true },
    { id: 'rank', label: t('Grade'), sortable: true },
    { id: 'lastname', label: t('Nom'), sortable: true },
    { id: 'firstname', label: t('Prénom'), sortable: true },
    { id: 'nicknameColor', label: t('Couleur'), sortable: false },
    { id: 'sex', label: t('Sexe'), sortable: true },
    { id: 'language', label: t('Langue'), sortable: true },
    { id: 'service', label: t('Service'), sortable: true },
    { id: 'opj', label: t('OPJ ?'), sortable: true },
    { id: 'opa', label: t('OPA ?'), sortable: true },
    { id: 'moderator', label: t('Modérateur ?'), sortable: true },
    { id: 'isBlocked', label: t('Statut'), sortable: true },
  ]), [t]);

  // Récupère le header d'authentification (token admin)
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Charger les utilisateurs
  const fetchUsers = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get('/api/user', { headers });
      setUsers(response.data);
    } catch (error) {
      setErrorMessage('Erreur lors du chargement des utilisateurs.');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Gestion de la recherche
  const handleSearchChange = (e) => setSearch(e.target.value);

  const filteredUsers = useMemo(() => {
    const searchLower = search.toLowerCase();
    const includesSearch = (value) => (value ?? '').toString().toLowerCase().includes(searchLower);
    return users.filter((user) => (
      includesSearch(user.matricule) ||
      includesSearch(user.lastname) ||
      includesSearch(user.firstname) ||
      includesSearch(user.rank)
    ));
  }, [users, search]);

  const getComparableValue = (user, property) => {
    if (['opj', 'opa', 'moderator', 'isBlocked'].includes(property)) {
      return user[property] ? 1 : 0;
    }
    if (property === 'sex') {
      const s = (user.sex || '').toString().toLowerCase();
      if (s === 'homme') return 'm';
      if (s === 'femme') return 'f';
      return s;
    }
    const value = user[property];
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'number') {
      return value;
    }
    return value.toString().toLowerCase();
  };

  const sortedUsers = useMemo(() => {
    const stabilized = filteredUsers.map((user, index) => [user, index]);
    stabilized.sort((a, b) => {
      const valueA = getComparableValue(a[0], orderBy);
      const valueB = getComparableValue(b[0], orderBy);

      if (valueA < valueB) {
        return order === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === 'asc' ? 1 : -1;
      }
      return a[1] - b[1];
    });
    return stabilized.map((item) => item[0]);
  }, [filteredUsers, order, orderBy]);

  const handleRequestSort = (property) => {
    if (orderBy === property) {
      setOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrder('asc');
      setOrderBy(property);
    }
  };

  const renderCellContent = (user, columnId) => {
    switch (columnId) {
      case 'nicknameColor':
        return (
          <Box component="span" sx={{ color: user.nicknameColor, fontWeight: 'bold' }}>
            &#11044;
          </Box>
        );
      case 'sex': {
        const s = (user.sex || '').toString().toLowerCase();
        if (s === 'm' || s === 'homme') return t('Homme');
        if (s === 'f' || s === 'femme') return t('Femme');
        return user.sex;
      }
      case 'language':
        return user.language || 'fr';
      case 'opj':
      case 'opa':
      case 'moderator':
        return user[columnId] ? t('Oui') : t('Non');
      case 'isBlocked':
        return user.isBlocked ? t('Bloqué') : t('Actif');
      default:
        return user[columnId];
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post('/api/user', formData, { headers });
      setSuccessMessage('Utilisateur ajouté avec succès.');
      fetchUsers();
      resetForm();
    } catch (error) {
      setErrorMessage('Erreur lors de l\'ajout de l\'utilisateur.');
    }
  };

  const handleDelete = async (id) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`/api/user/${id}`, { headers });
      setSuccessMessage('Utilisateur supprimé avec succès.');
      fetchUsers();
    } catch (error) {
      setErrorMessage('Erreur lors de la suppression de l\'utilisateur.');
    }
  };

  const handleBlock = async (id, isBlocked) => {
    try {
      const endpoint = isBlocked
        ? `/api/user/${id}/unblock`
        : `/api/user/${id}/block`;
      const headers = getAuthHeaders();
      await axios.patch(endpoint, null, { headers });
      setSuccessMessage(`Utilisateur ${isBlocked ? 'débloqué' : 'bloqué'} avec succès.`);
      fetchUsers();
    } catch (error) {
      setErrorMessage('Erreur lors du blocage/déblocage de l\'utilisateur.');
    }
  };

  const handleEdit = (user) => {
    setIsEditing(true);
    setFormData({
      matricule: user.matricule,
      password: '',
      lastname: user.lastname,
      firstname: user.firstname,
      service: user.service,
      rank: normalizeRankForForm(user.rank),
      sex: user.sex,
      language: user.language || 'fr',
      opj: user.opj,
      opa: user.opa,
      moderator: user.moderator,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const { matricule, ...updateData } = formData;
      const payload = { ...updateData };

      // En mode édition, ne mettre à jour le mot de passe que s'il est explicitement renseigné.
      if (typeof payload.password !== 'string' || payload.password.trim() === '') {
        delete payload.password;
      }

      const headers = getAuthHeaders();
      await axios.put(`/api/user/${matricule}`, payload, { headers });
      setSuccessMessage('Utilisateur modifié avec succès.');
      fetchUsers();
      resetForm();
    } catch (error) {
      setErrorMessage('Erreur lors de la modification de l\'utilisateur.');
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setFormData({
      matricule: '',
      password: '',
      lastname: '',
      firstname: '',
      service: '',
      rank: '',
      sex: '',
      language: 'fr',
      opj: false,
      opa: false,
      moderator: false,
    });
  };

  // Gestion de la boîte de dialogue de confirmation
  const handleOpenDialog = (user) => {
    setUserToDelete(user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setUserToDelete(null);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      handleDelete(userToDelete._id);
    }
    handleCloseDialog();
  };

  const totalUsers = users.length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des utilisateurs')}
          </Typography>
      
      <Info type="user" />

      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{t(errorMessage)}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{t(successMessage)}</Alert>}

      {/* Formulaire */}
      <Box component="form" onSubmit={isEditing ? handleUpdate : handleSubmit} autoComplete="off" sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {isEditing ? t('Modifier') : t('Ajouter')} {t('un nouvel utilisateur')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('Matricule')}
              name="matricule"
              value={formData.matricule}
              onChange={handleChange}
              inputProps={{ minLength: 9, maxLength: 9 }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('Mot de passe')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required={!isEditing}
              autoComplete="new-password"
              helperText={isEditing ? t('Laisser vide pour conserver le mot de passe actuel') : ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('Nom')}
              name="lastname"
              value={formData.lastname}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('Prénom')}
              name="firstname"
              value={formData.firstname}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="rank-label">{t('Grade')}</InputLabel>
              <Select
                labelId="rank-label"
                id="rank-select"
                name="rank"
                value={formData.rank}
                onChange={handleChange}
                label={t('Grade')}
                required
              >
                {gradeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
                {formData.rank && !gradeOptions.some((option) => option.value === formData.rank) && (
                  <MenuItem value={formData.rank}>
                    {formData.rank}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('Service')}
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="language-label">{t('Langue')}</InputLabel>
              <Select
                labelId="language-label"
                id="language-select"
                name="language"
                value={formData.language}
                onChange={handleChange}
                label={t('Langue')}
                required
              >
                <MenuItem value="fr">{t('Français')}</MenuItem>
                <MenuItem value="nl">{t('Néerlandais')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <RadioGroup row name="sex" value={formData.sex} onChange={handleChange}>
              <FormControlLabel value="m" control={<Radio />} label={t('M')} />
              <FormControlLabel value="f" control={<Radio />} label={t('F')} />
            </RadioGroup>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Checkbox checked={formData.opj} onChange={handleCheckboxChange} name="opj" />}
              label={t('Qualité OPJ')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Checkbox checked={formData.opa} onChange={handleCheckboxChange} name="opa" />}
              label={t('Qualité OPA')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Checkbox checked={formData.moderator} onChange={handleCheckboxChange} name="moderator" />}
              label={t('Modérateur')}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={isEditing ? handleUpdate : handleSubmit}
              sx={{ mr: 2 }}
            >
              {t(isEditing ? 'Modifier' : 'Ajouter')}
            </Button>
            {isEditing && (
              <Button variant="outlined" color="secondary" onClick={resetForm}>
                {t('Annuler')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Liste des utilisateurs */}
      <Typography variant="h6" gutterBottom>
        {t('Liste des utilisateurs')} ({totalUsers} {t('au total')})
      </Typography>
      <TextField
        fullWidth
        label={t('Rechercher un utilisateur')}
        placeholder={t('Matricule, grade, nom ou prénom')}
        value={search}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
      />
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              <TableCell
                sx={{
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  backgroundColor: 'background.paper',
                  boxShadow: '-4px 0 6px -6px rgba(0,0,0,0.4)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('Actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => (
              <TableRow key={user._id}>
                {columns.map((column) => (
                  <TableCell key={`${user._id}-${column.id}`}>
                    {renderCellContent(user, column.id)}
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    backgroundColor: 'background.paper',
                    boxShadow: '-4px 0 6px -6px rgba(0,0,0,0.4)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                    <IconButton size="small" onClick={() => handleBlock(user._id, user.isBlocked)}>
                      {user.isBlocked ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(user)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Boîte de dialogue de confirmation */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t('Confirmer la suppression')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("Êtes-vous sûr de vouloir supprimer l'utilisateur")} {userToDelete?.lastname} {userToDelete?.firstname} ({userToDelete?.matricule}) ? {t('Cette action est irréversible.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {t('Annuler')}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            {t('Supprimer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default UserAdmin;